import { useState } from 'react';
import axios from 'axios';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const baseColors = {
  bg: '#020617',
  panel: '#0f172a',
  border: '#1e293b',
  primary: '#38bdf8',
  text: '#e5e7eb',
  muted: '#94a3b8',
  danger: '#f87171'
};

const inputStyle = {
  background: baseColors.bg,
  border: `1px solid ${baseColors.border}`,
  color: baseColors.text,
  borderRadius: '12px',
  padding: '12px',
  fontSize: '14px'
};

const AuthModal = ({ isOpen, onClose, mode, onModeChange }) => {
  const { login } = useAuth();

  const [step, setStep] = useState('email');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    code: ''
  });
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const resetForm = () => {
    setFormData({ email: '', password: '', code: '' });
    setStep('email');
    setCaptchaToken(null);
  };

  // ---------- LOGIN ----------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      login(res.data.token, res.data.user);
      toast.success('Login successful');
      onClose();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // ---------- REGISTER ----------
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, {
        email: formData.email
      });
      toast.success('Verification code sent');
      setStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/verify`, {
        email: formData.email,
        code: formData.code,
        password: formData.password
      });
      login(res.data.token, res.data.user);
      toast.success('Account created');
      onClose();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // ---------- FORGOT PASSWORD ----------
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      toast.error('Please complete the captcha');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email,
        captcha_token: captchaToken
      });

      toast.success('If the email exists, a reset link was sent');
      resetForm();
      onModeChange('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        style={{
          background: `linear-gradient(180deg, ${baseColors.bg}, ${baseColors.panel})`,
          borderRadius: '18px',
          border: `1px solid ${baseColors.border}`,
          boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          color: baseColors.text
        }}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontSize: '26px',
              fontWeight: 800,
              textAlign: 'center',
              letterSpacing: '0.4px'
            }}
          >
            🌀 LitX
          </DialogTitle>
          <p style={{ textAlign: 'center', color: baseColors.muted, fontSize: 13 }}>
            {mode === 'login'
              ? 'Sign in to your account'
              : mode === 'forgot'
              ? 'Reset your password'
              : step === 'email'
              ? 'Create a new account'
              : 'Verify your email'}
          </p>
        </DialogHeader>

        {/* ---------------- LOGIN ---------------- */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <Label>📧 Email</Label>
            <Input style={inputStyle} type="email" required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            <Label>🔒 Password</Label>
            <Input style={inputStyle} type="password" required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />

            <Button
              type="submit"
              disabled={loading}
              style={{
                background: baseColors.primary,
                color: baseColors.bg,
                borderRadius: '14px',
                fontWeight: 800
              }}
              className="w-full"
            >
              {loading ? 'Signing in…' : 'Login'}
            </Button>

            <div style={{ textAlign: 'center', fontSize: 13 }}>
              <button
                type="button"
                onClick={() => { resetForm(); onModeChange('forgot'); }}
                className="text-cyan-400 hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <div style={{ textAlign: 'center', fontSize: 13 }}>
              Don’t have an account?{' '}
              <button type="button" onClick={() => onModeChange('signup')}
                className="text-cyan-400 hover:underline">
                Sign up
              </button>
            </div>
          </form>
        )}

        {/* ---------------- FORGOT PASSWORD ---------------- */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <Label>📧 Email</Label>
            <Input style={inputStyle} type="email" required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            <Turnstile
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              onSuccess={setCaptchaToken}
              onExpire={() => setCaptchaToken(null)}
            />

            <Button
              type="submit"
              disabled={loading}
              style={{
                background: baseColors.primary,
                color: baseColors.bg,
                borderRadius: '14px',
                fontWeight: 800
              }}
              className="w-full"
            >
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>

            <div style={{ textAlign: 'center', fontSize: 13 }}>
              <button type="button"
                onClick={() => { resetForm(); onModeChange('login'); }}
                className="text-cyan-400 hover:underline">
                ← Back to login
              </button>
            </div>
          </form>
        )}

        {/* ---------------- SIGNUP ---------------- */}
        {mode === 'signup' && step === 'email' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <Label>📧 Email</Label>
            <Input style={inputStyle} type="email" required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            <Button type="submit" disabled={loading}
              style={{
                background: baseColors.primary,
                color: baseColors.bg,
                borderRadius: '14px',
                fontWeight: 800
              }}
              className="w-full">
              {loading ? 'Sending…' : 'Send Verification Code'}
            </Button>

            <div style={{ textAlign: 'center', fontSize: 13 }}>
              Already have an account?{' '}
              <button type="button" onClick={() => onModeChange('login')}
                className="text-cyan-400 hover:underline">
                Login
              </button>
            </div>
          </form>
        )}

        {mode === 'signup' && step === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <Label>🔢 Code</Label>
            <Input style={inputStyle}
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />

            <Label>🔒 Password</Label>
            <Input style={inputStyle} type="password" required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />

            <Button type="submit" disabled={loading}
              style={{
                background: baseColors.primary,
                color: baseColors.bg,
                borderRadius: '14px',
                fontWeight: 800
              }}
              className="w-full">
              {loading ? 'Verifying…' : 'Verify & Create Account'}
            </Button>

            <Button type="button" variant="ghost"
              onClick={() => setStep('email')}
              className="w-full text-cyan-400">
              Back
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;